import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { StipInstructionsEditor } from "@/components/StipInstructionsEditor";
import { isStipInstructionsEmpty } from "@/utils/stipInstructions";
import { staffUi } from "@/pages/applications/staff-ui-classes";

type InternalThread = {
  id: number;
  application: number;
  title: string;
  is_system_general: boolean;
  created_by: number | null;
  created_at: string;
  updated_at: string;
};

type MentionableUser = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
};

type InternalMessage = {
  id: number;
  thread: number;
  application: number;
  author: number | null;
  author_display: string;
  body: string;
  mention_user_ids: number[];
  mentioned_users?: { id: number; display_name: string }[];
  hubspot_note_id: string | null;
  hubspot_synced_at: string | null;
  hubspot_last_error: string | null;
  hubspot_attempt_count: number;
  created_at: string;
  updated_at: string;
};

type DealNotesRow = {
  id: number;
  application: number;
  body_html: string;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
};

type InternalAttachment = {
  id: number;
  filename: string;
  mime: string;
  size: number;
  created_at: string;
};

function displayName(u: MentionableUser) {
  const n = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return n || u.email;
}

export function InternalCollaborationSection({ applicationId }: { applicationId: number }) {
  const qc = useQueryClient();
  const [threadId, setThreadId] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [mentionIds, setMentionIds] = useState<number[]>([]);
  const [newThreadTitle, setNewThreadTitle] = useState("");
  const [dealNotesHtml, setDealNotesHtml] = useState("");
  const [dealNotesDirty, setDealNotesDirty] = useState(false);

  const threadsQuery = useQuery({
    queryKey: ["internal-threads", applicationId],
    queryFn: async () => {
      const { data } = await customInstance.get<InternalThread[]>(
        `/api/applications/${applicationId}/internal-threads/`,
      );
      return data;
    },
  });

  const mentionableQuery = useQuery({
    queryKey: ["internal-mentionable", applicationId],
    queryFn: async () => {
      const { data } = await customInstance.get<MentionableUser[]>(
        `/api/applications/${applicationId}/internal-mentionable-users/`,
      );
      return data;
    },
  });

  useEffect(() => {
    const rows = threadsQuery.data;
    if (!rows?.length) return;
    if (threadId == null || !rows.some((t) => t.id === threadId)) {
      const general = rows.find((t) => t.is_system_general) ?? rows[0];
      setThreadId(general.id);
    }
  }, [threadsQuery.data, threadId]);

  const messagesQuery = useQuery({
    queryKey: ["internal-thread-messages", applicationId, threadId],
    enabled: threadId != null,
    queryFn: async () => {
      const { data } = await customInstance.get<InternalMessage[]>(
        `/api/applications/${applicationId}/internal-threads/${threadId}/messages/`,
      );
      return data;
    },
  });

  const dealNotesQuery = useQuery({
    queryKey: ["internal-deal-notes", applicationId],
    queryFn: async () => {
      const { data } = await customInstance.get<DealNotesRow>(
        `/api/applications/${applicationId}/internal-deal-notes/`,
      );
      return data;
    },
  });

  useEffect(() => {
    if (dealNotesQuery.data && !dealNotesDirty) {
      setDealNotesHtml(dealNotesQuery.data.body_html || "");
    }
  }, [dealNotesQuery.data, dealNotesDirty]);

  const attachmentsQuery = useQuery({
    queryKey: ["internal-attachments", applicationId],
    queryFn: async () => {
      const { data } = await customInstance.get<InternalAttachment[]>(
        `/api/applications/${applicationId}/internal-attachments/`,
      );
      return data;
    },
  });

  const createThreadMutation = useMutation({
    mutationFn: async (title: string) => {
      const { data } = await customInstance.post<InternalThread>(
        `/api/applications/${applicationId}/internal-threads/`,
        { title },
      );
      return data;
    },
    onSuccess: (row) => {
      toast.success("Thread created");
      setNewThreadTitle("");
      qc.invalidateQueries({ queryKey: ["internal-threads", applicationId] });
      setThreadId(row.id);
    },
    onError: () => toast.error("Could not create thread"),
  });

  const postMessageMutation = useMutation({
    mutationFn: async (payload: { body: string; mention_user_ids: number[] }) => {
      const { data } = await customInstance.post<InternalMessage>(
        `/api/applications/${applicationId}/internal-threads/${threadId}/messages/`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      setDraft("");
      setMentionIds([]);
      qc.invalidateQueries({ queryKey: ["internal-thread-messages", applicationId, threadId] });
      qc.invalidateQueries({ queryKey: ["internal-threads", applicationId] });
      toast.success("Message sent");
    },
    onError: () => toast.error("Could not send message"),
  });

  const saveDealNotesMutation = useMutation({
    mutationFn: async (body_html: string) => {
      const { data } = await customInstance.patch<DealNotesRow>(
        `/api/applications/${applicationId}/internal-deal-notes/`,
        { body_html },
      );
      return data;
    },
    onSuccess: (row) => {
      setDealNotesHtml(row.body_html || "");
      setDealNotesDirty(false);
      qc.setQueryData(["internal-deal-notes", applicationId], row);
      toast.success("Internal deal notes saved");
    },
    onError: () => toast.error("Could not save notes"),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const { data: presign } = await customInstance.post<{
        presigned_url: string;
        s3_key: string;
      }>(`/api/applications/${applicationId}/internal-attachments/presign/`, {
        filename: file.name,
        mime_type: file.type,
      });
      const put = await fetch(presign.presigned_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) {
        throw new Error("S3 upload failed");
      }
      await customInstance.post(`/api/applications/${applicationId}/internal-attachments/confirm/`, {
        s3_key: presign.s3_key,
        filename: file.name,
        mime_type: file.type,
        size: file.size,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["internal-attachments", applicationId] });
      toast.success("File uploaded");
    },
    onError: () => toast.error("Upload failed"),
  });

  const toggleMention = (id: number) => {
    setMentionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const downloadAttachment = async (attId: number) => {
    try {
      const { data } = await customInstance.get<{ download_url: string }>(
        `/api/applications/${applicationId}/internal-attachments/${attId}/download-url/`,
      );
      window.open(data.download_url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Could not get download link");
    }
  };

  return (
    <div className="space-y-4">
      <Card className={staffUi.internalCard}>
        <CardHeader>
          <CardTitle className="text-base">Internal team discussion</CardTitle>
          <p className="text-sm text-muted-foreground">
            Visible only to DealerDog super admins and account managers. Synced to HubSpot as deal/contact notes.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Thread</label>
              <select
                className="border rounded-md px-2 py-1.5 text-sm bg-background min-w-[200px]"
                value={threadId ?? ""}
                onChange={(e) => setThreadId(Number(e.target.value))}
                disabled={!threadsQuery.data?.length}
              >
                {(threadsQuery.data || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                    {t.is_system_general ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-muted-foreground">New thread title</label>
              <div className="flex gap-2">
                <input
                  className="flex-1 border rounded-md px-2 py-1.5 text-sm bg-background"
                  value={newThreadTitle}
                  onChange={(e) => setNewThreadTitle(e.target.value)}
                  placeholder="e.g. UW questions"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!newThreadTitle.trim() || createThreadMutation.isPending}
                  onClick={() => createThreadMutation.mutate(newThreadTitle.trim())}
                >
                  Add thread
                </Button>
              </div>
            </div>
          </div>

          <div className="border rounded-md bg-background max-h-64 overflow-y-auto p-3 space-y-2 text-sm">
            {messagesQuery.isLoading ? (
              <p className="text-muted-foreground">Loading messages…</p>
            ) : (messagesQuery.data || []).length === 0 ? (
              <p className="text-muted-foreground">No messages yet.</p>
            ) : (
              (messagesQuery.data || []).map((m) => (
                <div key={m.id} className="border-b border-border/60 pb-2 last:border-0">
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                    <span>{new Date(m.created_at).toLocaleString()}</span>
                    <span>·</span>
                    <span className="font-medium text-foreground">{m.author_display || "Unknown"}</span>
                    {m.hubspot_synced_at ? (
                      <span className="text-green-700">· HubSpot synced</span>
                    ) : m.hubspot_last_error ? (
                      <span className="text-amber-800">· HubSpot pending/error</span>
                    ) : null}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
                  {(m.mentioned_users && m.mentioned_users.length > 0) ||
                  (m.mention_user_ids && m.mention_user_ids.length > 0) ? (
                    <p className="mt-2 text-xs">
                      <span className="font-semibold text-foreground/90">Tagged: </span>
                      <span className="text-indigo-900">
                        {(m.mentioned_users && m.mentioned_users.length > 0
                          ? m.mentioned_users.map((u) => `@${u.display_name}`)
                          : m.mention_user_ids.map((id) => `@user ${id}`)
                        ).join(", ")}
                      </span>
                    </p>
                  ) : null}
                </div>
              ))
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">
              Tag for notifications (optional)
            </label>
            <p className="text-xs text-muted-foreground">
              Click people below to notify them by email and in-app; tagged names also appear on your message for
              everyone in this thread. Typing @ in the message alone does not tag anyone.
            </p>
            {mentionIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 rounded-md border border-gray-200 bg-gray-100 px-2 py-2 text-xs">
                <span className="text-muted-foreground shrink-0 mr-1">Will notify:</span>
                {mentionIds.map((id) => {
                  const u = (mentionableQuery.data || []).find((x) => x.id === id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded-full bg-background px-2 py-0.5 border"
                    >
                      @{u ? displayName(u) : `user ${id}`}
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Remove mention"
                        onClick={() => toggleMention(id)}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {(mentionableQuery.data || []).map((u) => (
                <Button
                  key={u.id}
                  type="button"
                  size="sm"
                  variant={mentionIds.includes(u.id) ? "default" : "outline"}
                  className="h-8"
                  onClick={() => toggleMention(u.id)}
                >
                  @{displayName(u)}
                </Button>
              ))}
            </div>
            <textarea
              className="w-full min-h-[88px] border rounded-md px-3 py-2 text-sm bg-background"
              placeholder="Message…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button
              type="button"
              size="sm"
              disabled={
                postMessageMutation.isPending || threadId == null || !draft.trim()
              }
              onClick={() =>
                postMessageMutation.mutate({
                  body: draft.trim(),
                  mention_user_ids: mentionIds,
                })
              }
            >
              Send message
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className={staffUi.internalCard}>
        <CardHeader>
          <CardTitle className="text-base">Internal deal notes</CardTitle>
          <p className="text-sm text-muted-foreground">
            Single shared doc for DD account managers and super admins — not shown to dealers.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <StipInstructionsEditor
            value={dealNotesHtml}
            onChange={(html) => {
              setDealNotesHtml(html);
              setDealNotesDirty(true);
            }}
            placeholder="Deal narrative, coordination points…"
          />
          <Button
            type="button"
            size="sm"
            disabled={saveDealNotesMutation.isPending || !dealNotesDirty}
            onClick={() =>
              saveDealNotesMutation.mutate(
                isStipInstructionsEmpty(dealNotesHtml) ? "" : dealNotesHtml,
              )
            }
          >
            Save internal deal notes
          </Button>
        </CardContent>
      </Card>

      <Card className={staffUi.internalCard}>
        <CardHeader>
          <CardTitle className="text-base">Private internal attachments</CardTitle>
          <p className="text-sm text-muted-foreground">Not visible to org admins or sales reps.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            type="file"
            className="text-sm"
            disabled={uploadMutation.isPending}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) uploadMutation.mutate(f);
            }}
          />
          <div className="space-y-1 text-sm">
            {(attachmentsQuery.data || []).length === 0 ? (
              <p className="text-muted-foreground">No internal attachments.</p>
            ) : (
              (attachmentsQuery.data || []).map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-2 border rounded px-2 py-1 bg-background">
                  <span className="flex-1 truncate">{a.filename}</span>
                  <Button type="button" size="sm" variant="outline" onClick={() => downloadAttachment(a.id)}>
                    Download
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
